import {Injectable} from "@angular/core";
import {
  BuilderGeneratedPageSectionI,
  ColorI, FigmaScreenshotI, LogoParamsI,
  PageMetaI, ProductCategoriesI,
  SecondaryPageWithSectionsI,
  SectionTemplateI
} from "./builder-v2.interface";
import {BehaviorSubject} from "rxjs/internal/BehaviorSubject";
import {Subject} from "rxjs/internal/Subject";
import {FormControl} from "@angular/forms";
import {CommonBuilderDataService} from "./services/common-builder-data.service";
import {ReCaptchaV3Service} from 'ngx-captcha';
import {AiAssistantService} from '../ai-assistant/service/ai-assistant.service';
import {Config} from '../config/config';
import {takeUntil} from 'rxjs/operators';
import {UserService} from "../user/user.service";
import {CompanyInfo} from "../store";

@Injectable({
  providedIn: 'root'
})
export class BuilderSectionsInteractionService {
  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  minZoom: BehaviorSubject<number> = new BehaviorSubject<number>(30);
  maxZoom: BehaviorSubject<number> = new BehaviorSubject<number>(250);
  pageSections: BuilderGeneratedPageSectionI[] = [];
  defaultColor: BehaviorSubject<ColorI> = new BehaviorSubject<ColorI>({
    title: 'Indigo',
    primaryColor: '#283566',
    secondaryColor: '#986424'
  });
  activeColor: BehaviorSubject<ColorI> = new BehaviorSubject<ColorI>({
    title: 'Indigo',
    primaryColor: '#283566',
    secondaryColor: '#986424'
  });
  activeFont: BehaviorSubject<string> = new BehaviorSubject<string>('Montserrat');
  activeTheme: BehaviorSubject<string> = new BehaviorSubject<string>('Flat');
  websiteCreationPrompt: BehaviorSubject<string> = new BehaviorSubject<string>('');
  aiGeneratedStyle;
  sectionChanges: Subject<{
    sections: BuilderGeneratedPageSectionI[],
    changedIndex?: number
  }> = new Subject<{
    sections: BuilderGeneratedPageSectionI[],
    changedIndex?: number
  }>();
  saveCustomizationChanges: Subject<void> = new Subject<void>();
  siteInfo: BehaviorSubject<{
    business_name: string,
    business_description: string,
    generate_secondary_pages?: boolean,
    business_type: string,
    website_type?: string,
    domain_name?: string,
    analysis?: string,
    figma_url?: string,
    image_url?: string[],
    initial_description?: string,
  }> = new BehaviorSubject<{
    business_name: string,
    business_description: string,
    generate_secondary_pages?: boolean,
    website_type?: string,
    analysis?: string,
    figma_url?: string,
    image_url?: string[],
    business_type: string,
  }>(null);
  websiteInfo: {
    website_description: string;
    website_title: string;
    website_keyphrase: string;
    website_design_type_level: number
  };
  updateSectionTypes: BehaviorSubject<BuilderGeneratedPageSectionI[]> = new BehaviorSubject<BuilderGeneratedPageSectionI[]>([]);
  updateSection: BehaviorSubject<BuilderGeneratedPageSectionI[]> = new BehaviorSubject<BuilderGeneratedPageSectionI[]>([]);
  updateProductCategories: BehaviorSubject<ProductCategoriesI> = new BehaviorSubject<ProductCategoriesI>(null);
  buildLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  generateDescriptionLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  generatePrefilledSiteLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  prepareDialogForFreshOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  discoverBusinessDataReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  saveLocalState: Subject<void> = new Subject();
  pageLoaded: Subject<void | boolean> = new Subject<void | boolean>();
  scrollToSection: Subject<string> = new Subject<string>();
  requestSubject: Subject<{ action: string, response: any }> = new Subject<{ action: string, response: any }>();
  updateEnhancedSection: Subject<{
    index: number,
    section_title: string,
    section_description: string
  }> = new Subject<{
    index: number,
    section_title: string,
    section_description: string
  }>();
  enhanceSection: Subject<{ type: 'new' | 'enhance' | 'title' | 'description', index: number }> = new Subject<{
    type: 'enhance',
    index: number
  }>();
  showArcade: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showRightClick = true;
  isSectionEdit = false;
  canAddSection: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  secondaryPageLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  secondaryPages: BehaviorSubject<SecondaryPageWithSectionsI[]> = new BehaviorSubject<SecondaryPageWithSectionsI[]>([]);
  changePageOutline: Subject<{ action: 'add' | 'remove'; template: SectionTemplateI; tempo: boolean; index?: number }> =
    new Subject<{ action: 'add' | 'remove'; template: any; tempo: boolean; index?: number }>();
  disableNextButton: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  showSectionsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  hidePrefilledAnimations: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  secondaryPagesLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  params: BehaviorSubject<{
    business_type?: string;
    formData?: any;
    domainInfo?: any;
  }> = new BehaviorSubject<{
    business_type?: string;
    formData?: any;
    domainInfo?: any;
  }>(null);
  businessCategory: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  websiteType: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private backAction: () => void = null;
  enhancedDescriptionIsDone: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public enhancedDescription$ = new BehaviorSubject<string | null>(null); // Observable to track `enhancedDescription`
  prefilledDescription: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  heroSectionData: BehaviorSubject<{
    "input": string,              // Required, max 500 chars - business description
    "industry": string,           // Optional - industry context
    "square": boolean,           // Optional, default false - if true generates square image
    "website_type": string       // Optional, enum: ["ecommerce", "basic"], default "basic"
  }> = new BehaviorSubject<{
    "input": string,              // Required, max 500 chars - business description
    "industry": string,           // Optional - industry context
    "square": boolean,           // Optional, default false - if true generates square image
    "website_type": string       // Optional, enum: ["ecommerce", "basic"], default "basic"
  }>(null);
  brandKitOptions: BehaviorSubject<LogoParamsI> = new BehaviorSubject<LogoParamsI>({} as LogoParamsI);
  brandKitLogos: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private brandKitNotifSubject = new Subject<void>();
  brandKitFinalData: any;
  templates: any[] = [];
  selectedLogo: any;
  LogoGenerator = false;
  generatedLogo: any;
  selectedBrand: any;
  vibeCodingImages = [];
  vibeCodingFigma: FigmaScreenshotI = {} as FigmaScreenshotI;
  vibeCodingLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  companyInfo: BehaviorSubject<CompanyInfo> = new BehaviorSubject<CompanyInfo>(null);

  // Available fonts for randomization
  private readonly availableFonts = [
    "Outfit", "Lexend Peta", "Lora", "DM Sans", "Charm",
    "Montserrat", "Cinzel", "Playfair Display",
    "Merienda", "Akaya Kanadaka", "Michroma", "Climate Crisis"
  ];

  /**
   * Get a random font from the available fonts array
   * @returns A randomly selected font name
   */
  private getRandomFont(): string {
    const randomIndex = Math.floor(Math.random() * this.availableFonts.length);
    return this.availableFonts[randomIndex];
  }

  constructor(
    private commonService: CommonBuilderDataService,
    private _reCaptchaV3Service: ReCaptchaV3Service,
    private _aiAssistantService: AiAssistantService,
    private _userService: UserService,
  ) {
  }

  checkAddLimit(sectionsCount: number, limit: number) {
    this.canAddSection.next(sectionsCount < limit);
  }

  resetSubjects() {
    this.loading.next(false);
    this.minZoom.next(30);
    this.maxZoom.next(250);
    this.pageSections = [];
    this.defaultColor.next({
      title: 'Indigo',
      primaryColor: '#283566',
      secondaryColor: '#986424'
    });
    this.activeColor.next({
      title: 'Indigo',
      primaryColor: '#283566',
      secondaryColor: '#986424'
    });
    this.activeFont.next('Montserrat');
    this.activeTheme.next('Flat');
    this.aiGeneratedStyle = null;
    this.siteInfo.next(null);
    this.updateSectionTypes.next([]);
    this.buildLoading.next(false);
    this.generateDescriptionLoading.next(false);
    this.showArcade.next(false);
    this.isSectionEdit = false;
    this.secondaryPageLoading.next(false);
    this.secondaryPagesLoaded.next(false);
    this.brandKitOptions.next({} as LogoParamsI);
    localStorage.removeItem('dbBrandKitData'); // Clear brand kit data when resetting
    this.prepareDialogForFreshOpen.next(false);
    this.discoverBusinessDataReady.next(false);
  }

  addFormControls(sections: BuilderGeneratedPageSectionI[]): BuilderGeneratedPageSectionI[] {
    return sections?.map(section => {
      section.description_control = new FormControl(section.section_description);
      section.title_control = new FormControl(section.section_title);

      return section;
    });
  }

  removeFormControls(sections: BuilderGeneratedPageSectionI[]): BuilderGeneratedPageSectionI[] {
    return sections.map(({description_control, title_control, ...rest}) => rest);
  }

  getModifiedSectionStructure(sections: BuilderGeneratedPageSectionI[], index: number = null, updateFields = false) {
    sections = sections.map(({loading, ...section}, index) => {
      section.index = index;
      if (updateFields) {
        section.section_title = section.title_control?.value;
        section.section_description = section.description_control?.value;
      }
      return section;
    });
    return {
      "page_outline": {
        sections: this.removeFormControls(sections)
      },
      "section_index": index,
      "business_type": this.siteInfo?.value?.business_type,
      "website_type": this.commonService.websiteType,
      ecom_data: this.updateProductCategories.value,
      "business_name": this.siteInfo?.value?.business_name,
      "business_description": this.siteInfo?.value?.business_description
    };
  }

  removeExtraKeysFromSections(sections: BuilderGeneratedPageSectionI[], onlyBase = false, leaveSectionType = false) {
    return sections.map(({description_control, title_control, is_tempo, loading, ...mainSection}) => {
      if (onlyBase) {
        const {variation, reasoning, section_type, ...base} = mainSection;
        if (leaveSectionType) {
          base['section_type'] = section_type;
        }
        return base;
      }
      return mainSection;
    });
  }

  getPagesMeta(removeAllExtraKeys = true) {
    return this.secondaryPages.value.map(({page, sections}) => {
      sections = sections.map(section => {
        section.section_description = section.description_control?.value || section.section_description;
        section.section_title = section.title_control?.value || section.section_title;
        return section;
      });
      sections = this.removeExtraKeysFromSections(this.removeFormControls(sections), removeAllExtraKeys);
      return {
        ...page,
        sections
      };
    });
  }

  getSinglePageGenerationData(removeAllExtraKeys = true) {
    const pages = this.secondaryPages.value.map(({page, sections}) => {
      sections = sections.map(section => {
        section.section_description = section.description_control?.value || section.section_description;
        section.section_title = section.title_control?.value || section.section_title;
        return section;
      });
      sections = this.removeExtraKeysFromSections(this.removeFormControls(sections), removeAllExtraKeys, true);
      return {
        sections
      };
    });
    const page = pages?.at(0);
    if (page) {
      page.sections.shift();
      page.sections.pop();
      const sections = page.sections.map((section, i) => {
        section.index = i;
        return section;
      });
      return {sections};
    }
  }

  convertToSecondaryPages(pageMeta: PageMetaI[]): SecondaryPageWithSectionsI[] {
    return pageMeta.map((resp, index) => {
      return {
        page: pageMeta[index],
        sections: resp.sections
      };
    });
  }

  setBackAction(action: () => void): void {
    this.backAction = action;
  }

  getBackAction(): () => void {
    return this.backAction;
  }

  clearBackAction(): void {
    this.backAction = null;
  }

  saveWebsiteTypeMobile(websiteType): void {
    this.websiteType.next(websiteType);
    sessionStorage.setItem('websiteType', websiteType);
  }

  getWebsiteTypeMobile(): string {
    return sessionStorage.getItem('websiteType') || 'basic';
  }

  transformOldFormData(data: any): any {
    const transformedData = {
      business_name: data.store_name || data.business_name || 'string',
      business_type: data.industry_name || 'string',
      business_description: data.site_description || data.business_description || 'string',
      website_type: data['website_type']
    };

    return transformedData;
  }

  setEnhancedDescription(description: string) {
    this.enhancedDescription$.next(description);
  }

  /**
   * Save brand kit data for a website
   * @param websiteId - The website ID
   */
  saveBrandKitData(websiteId: number): void {
    const brandKitData = {
      options: this.brandKitOptions.value,
      selectedLogo: this.selectedLogo,
      selectedBrand: this.selectedBrand,
      generatedLogo: this.generatedLogo
    };
    localStorage.setItem('dbBrandKitData', JSON.stringify(brandKitData));
  }

  /**
   * Get brand kit request token
   * @param websiteId - The website ID
   * @returns Observable with the token
   */
  getBrandKitRequestToken(websiteId: number, callback?: (token: string) => void): void {
    // Generate a simple token or retrieve from service
    const token = `brandkit_${websiteId}_${Date.now()}`;
    if (callback) {
      callback(token);
    }
  }
}
